import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";
import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  createPeriod,
  deletePeriod,
  fetchPeriods,
  updatePeriodStatus,
  type PeriodRow,
  type PeriodStatus,
} from "@/lib/periods-api";
import { getAccessToken } from "@/lib/local-auth";
import { uploadTimesheetSummary } from "@/lib/timesheets-api";
import { importTimesheetToRate, type TimesheetEmployeeImport } from "@/lib/rate-api";
import { extractDateRangeFromFilename, sameYmd, type TimesheetUploadSummaryResponse } from "@/lib/timesheet-shared";
import {
  exportTelegramNotesMarkdown,
  getTelegramDualExportBodies,
  saveAdditingHoursFromNotes,
  saveReimburseFromNotes,
  telegramMarkdownFilenamesForMonth,
} from "@/lib/telegram-notes-api";

export const Route = createFileRoute("/periode")({
  component: PeriodePage,
});

const CREATE_FLOW_STEPS = [
  { id: "period", label: "Periode disimpan" },
  { id: "excel", label: "File Excel dibaca dan diringkas" },
  { id: "rates", label: "Ringkasan Hivedesk masuk penggajian" },
  { id: "tg_reimburse", label: "Ekspor Telegram reimburse → Markdown" },
  { id: "tg_hivedesk", label: "Ekspor Telegram Hivedesk → Markdown" },
  { id: "save_hours", label: "Jam tambahan dari notes masuk database" },
  { id: "save_reimburse_notes", label: "Reimburse dari notes masuk database" },
] as const;

type FlowStepState = "pending" | "running" | "done" | "error";

const STATUSES: { value: PeriodStatus; label: string }[] = [
  { value: "draft", label: "draft" },
  { value: "open", label: "open" },
  { value: "locked", label: "locked" },
  { value: "closed", label: "closed" },
  { value: "paid", label: "paid" },
];

function isValidYmd(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return false;
  const [y, m, day] = value.split("-").map(Number);
  return d.getUTCFullYear() === y && d.getUTCMonth() + 1 === m && d.getUTCDate() === day;
}

function compareYmd(a: string, b: string): number {
  return new Date(`${a}T00:00:00Z`).getTime() - new Date(`${b}T00:00:00Z`).getTime();
}

function parseYmd(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function ymd(y: number, m1: number, d: number): string {
  const dt = new Date(y, m1 - 1, d);
  if (Number.isNaN(dt.getTime())) return "";
  return format(dt, "yyyy-MM-dd");
}

function computePayrollRangeFromMonthYear(year: number, month1: number): { start: string; end: string } {
  // end_date: 24 di bulan yang dipilih
  // start_date: 25 di bulan sebelumnya
  const prev = new Date(year, month1 - 2, 1);
  const prevY = prev.getFullYear();
  const prevM1 = prev.getMonth() + 1;
  return {
    start: ymd(prevY, prevM1, 25),
    end: ymd(year, month1, 24),
  };
}

function monthTitleFromEndDate(endDate: string): string {
  const d = parseYmd(endDate);
  if (!d) return endDate;
  const label = d.toLocaleString("id-ID", { month: "long", year: "numeric" });
  return label.length ? label[0].toUpperCase() + label.slice(1) : label;
}

function formatRangeId(p: Pick<PeriodRow, "start_date" | "end_date">) {
  const start = parseYmd(p.start_date);
  const end = parseYmd(p.end_date);
  if (!start || !end) return `${p.start_date} → ${p.end_date}`;
  return `${format(start, "dd-MM-yyyy")} → ${format(end, "dd-MM-yyyy")}`;
}

function PeriodePage() {
  const [rows, setRows] = useState<PeriodRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PeriodRow | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [openCreate, setOpenCreate] = useState(false);
  const now = new Date();
  const [pickedYear, setPickedYear] = useState(String(now.getFullYear()));
  const [pickedMonth, setPickedMonth] = useState(String(now.getMonth() + 1).padStart(2, "0"));
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState<PeriodStatus>("draft");
  const [timesheetFile, setTimesheetFile] = useState<File | null>(null);
  const timesheetInputRef = useRef<HTMLInputElement>(null);
  const [submittingCreate, setSubmittingCreate] = useState(false);
  const [flowStepStates, setFlowStepStates] = useState<FlowStepState[]>(() => CREATE_FLOW_STEPS.map(() => "pending"));

  function resetCreateDialogForm() {
    setTimesheetFile(null);
    if (timesheetInputRef.current) timesheetInputRef.current.value = "";
    setFlowStepStates(CREATE_FLOW_STEPS.map(() => "pending"));
    setStatus("draft");
  }

  /** Mencegah menutup dialog saat masih memproses (Esc / overlay). Tutupan dari kode dapat memanggil reset + setOpen secara langsung. */
  function handleCreateDialogOpenChange(open: boolean) {
    if (!open && submittingCreate) return;
    setOpenCreate(open);
    if (!open) resetCreateDialogForm();
  }

  useEffect(() => {
    const y = Number(pickedYear);
    const m1 = Number(pickedMonth);
    if (!Number.isFinite(y) || !Number.isFinite(m1) || m1 < 1 || m1 > 12) return;
    const r = computePayrollRangeFromMonthYear(y, m1);
    setStartDate(r.start);
    setEndDate(r.end);
  }, [pickedMonth, pickedYear]);

  const sorted = useMemo(() => [...rows].sort((a, b) => compareYmd(b.start_date, a.start_date)), [rows]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchPeriods();
      setRows(data);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gagal memuat periode");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const submitCreate = async () => {
    if (!getAccessToken()) {
      toast.error("Belum login atau token tidak tersedia.");
      return;
    }
    if (!timesheetFile) {
      toast.error("Pilih file Excel Hivedesk (.xlsx) terlebih dahulu.");
      return;
    }
    if (!isValidYmd(startDate) || !isValidYmd(endDate)) {
      toast.error("Tanggal periode tidak valid.");
      return;
    }
    if (compareYmd(endDate, startDate) < 0) {
      toast.error("Tanggal akhir harus setelah atau sama dengan tanggal mulai.");
      return;
    }

    const r = extractDateRangeFromFilename(timesheetFile.name);
    if (r) {
      const matches = sameYmd(r.start, startDate) && sameYmd(r.end, endDate);
      if (!matches) {
        toast.error(
          `Rentang tanggal dalam nama file (${r.start} → ${r.end}) harus sama dengan periode (${startDate} → ${endDate}).`,
        );
        return;
      }
    }

    const bump = (i: number, s: FlowStepState) =>
      setFlowStepStates((prev) => {
        const n = [...prev];
        n[i] = s;
        return n;
      });

    setSubmittingCreate(true);
    setFlowStepStates(CREATE_FLOW_STEPS.map(() => "pending"));

    const rollbackPeriod = async (id: string): Promise<void> => {
      try {
        await deletePeriod(id);
      } catch {
        toast.error("Periode gagal dihapus otomatis setelah error — hapus entri di daftar secara manual bila muncul.");
      }
    };

    let created: PeriodRow | null = null;

    bump(0, "running");
    try {
      created = await createPeriod({ start_date: startDate, end_date: endDate, status });
    } catch (e) {
      bump(0, "error");
      toast.error(e instanceof Error ? e.message : "Gagal menyimpan periode");
      setSubmittingCreate(false);
      return;
    }
    bump(0, "done");

    bump(1, "running");
    let resp: TimesheetUploadSummaryResponse;
    try {
      resp = (await uploadTimesheetSummary(timesheetFile)) as TimesheetUploadSummaryResponse;
    } catch (e) {
      bump(1, "error");
      await rollbackPeriod(created!.id);
      toast.error(e instanceof Error ? e.message : "Gagal membaca file Excel");
      setSubmittingCreate(false);
      return;
    }
    bump(1, "done");

    bump(2, "running");
    const emps = resp.employees;
    if (!Array.isArray(emps) || emps.length === 0) {
      bump(2, "error");
      await rollbackPeriod(created!.id);
      toast.error("File Excel tidak berisi ringkasan karyawan.");
      setSubmittingCreate(false);
      return;
    }

    const payload: TimesheetEmployeeImport[] = emps.map((e) => ({
      name: e.name,
      hourlyRate: e.hourlyRate,
      totals: e.totals,
      projects: e.projects,
    }));

    try {
      await importTimesheetToRate(created!.id, payload, resp.report);
    } catch (e) {
      bump(2, "error");
      await rollbackPeriod(created!.id);
      toast.error(e instanceof Error ? e.message : "Gagal menyimpan ringkasan ke penggajian");
      setSubmittingCreate(false);
      return;
    }
    bump(2, "done");

    /** Periode telah valid di backend; hindari duplikat baris jika gagal bertahap atau sukses. */
    const persistCreatedRow = () =>
      setRows((prev) => (prev.some((p) => p.id === created!.id) ? prev : [created!, ...prev]));

    const year = Number(pickedYear);
    const monthNum = Number(pickedMonth);
    if (!Number.isFinite(year) || !Number.isFinite(monthNum) || monthNum < 1 || monthNum > 12) {
      bump(3, "error");
      toast.error("Tahun/bulan periode tidak valid untuk sinkron Telegram.");
      setSubmittingCreate(false);
      persistCreatedRow();
      return;
    }

    const mdNames = telegramMarkdownFilenamesForMonth(monthNum, year);
    const dualExports = getTelegramDualExportBodies({
      month1: monthNum,
      year,
      reimburseFilename: mdNames.reimburse,
      hivedeskFilename: mdNames.hivedesk,
    });
    const [exReimburse, exHive] = dualExports;
    if (!exReimburse || !exHive) {
      bump(3, "error");
      toast.error("Konfigurasi export Telegram tidak lengkap.");
      setSubmittingCreate(false);
      persistCreatedRow();
      return;
    }

    bump(3, "running");
    try {
      await exportTelegramNotesMarkdown(exReimburse.payload);
    } catch (e) {
      bump(3, "error");
      toast.error(e instanceof Error ? e.message : "Gagal ekspor Telegram reimburse");
      setSubmittingCreate(false);
      persistCreatedRow();
      return;
    }
    bump(3, "done");

    bump(4, "running");
    try {
      await exportTelegramNotesMarkdown(exHive.payload);
    } catch (e) {
      bump(4, "error");
      toast.error(e instanceof Error ? e.message : "Gagal ekspor Telegram Hivedesk");
      setSubmittingCreate(false);
      persistCreatedRow();
      return;
    }
    bump(4, "done");

    const periodNumericId = Number(created!.id);

    bump(5, "running");
    try {
      await saveAdditingHoursFromNotes({
        period_id: periodNumericId,
        filename: mdNames.hivedesk,
        mode: "append",
      });
    } catch (e) {
      bump(5, "error");
      toast.error(e instanceof Error ? e.message : "Gagal simpan jam tambahan dari notes");
      setSubmittingCreate(false);
      persistCreatedRow();
      return;
    }
    bump(5, "done");

    bump(6, "running");
    try {
      await saveReimburseFromNotes({
        period_id: periodNumericId,
        filename: mdNames.reimburse,
        mode: "append",
      });
    } catch (e) {
      bump(6, "error");
      toast.error(e instanceof Error ? e.message : "Gagal simpan reimburse dari notes");
      setSubmittingCreate(false);
      persistCreatedRow();
      return;
    }
    bump(6, "done");

    persistCreatedRow();
    toast.success(
      "Periode dibuat: Hivedesk, ekspor Telegram, jam tambahan, dan reimburse notes telah diproses.",
    );
    setSubmittingCreate(false);
    resetCreateDialogForm();
    setOpenCreate(false);
  };

  const changeStatus = async (id: string, next: PeriodStatus) => {
    setBusyId(id);
    try {
      const updated = await updatePeriodStatus(id, next);
      setRows((prev) => prev.map((p) => (p.id === id ? updated : p)));
      toast.success("Status periode diperbarui");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gagal update status");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id: string) => {
    setDeleteLoading(true);
    try {
      await deletePeriod(id);
      setRows((prev) => prev.filter((p) => p.id !== id));
      toast.success("Periode dihapus");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gagal menghapus periode");
    } finally {
      setDeleteLoading(false);
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Periode Penggajian</h1>
          <p className="mt-1.5 max-w-xl text-sm leading-snug text-muted-foreground">
            Periode mengelompokkan penggajian per rentang tanggal (rate, jam, slip). Buat periode baru, atur status alur, lalu buka detail untuk mengisi data tiap siklus.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => void load()} disabled={loading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Muat ulang
          </Button>
          <Dialog open={openCreate} onOpenChange={handleCreateDialogOpenChange}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Buat Periode
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Buat Periode</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Bulan</Label>
                    <Select value={pickedMonth} onValueChange={setPickedMonth}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 12 }).map((_, idx) => {
                          const m1 = idx + 1;
                          const v = String(m1).padStart(2, "0");
                          const label = new Date(2026, idx, 1).toLocaleDateString("id-ID", { month: "long" });
                          return (
                            <SelectItem key={v} value={v}>
                              {label[0].toUpperCase() + label.slice(1)}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Tahun</Label>
                    <Select value={pickedYear} onValueChange={setPickedYear}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 7 }).map((_, idx) => {
                          const y = String(now.getFullYear() - 2 + idx);
                          return (
                            <SelectItem key={y} value={y}>
                              {y}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Tanggal mulai</Label>
                    <Input value={startDate ? format(new Date(`${startDate}T00:00:00`), "dd-MM-yyyy") : "-"} readOnly disabled={true}/>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Tanggal akhir</Label>
                    <Input value={endDate ? format(new Date(`${endDate}T00:00:00`), "dd-MM-yyyy") : "-"} readOnly disabled={true}/>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Status</Label>
                  <Select value={status} onValueChange={(v) => setStatus(v as PeriodStatus)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">Upload Excel Hivedesk</Label>
                  <p className="text-xs text-muted-foreground">
                    File .xlsx dari Hivedesk. Jika nama file memuat rentang tanggal, harus cocok dengan tanggal periode di atas.
                  </p>
                  <input
                    ref={timesheetInputRef}
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    className="hidden"
                    id="create-periode-timesheet"
                    disabled={submittingCreate}
                    onChange={(e) => {
                      const f = e.target.files?.[0] ?? null;
                      setTimesheetFile(f);
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!getAccessToken() || submittingCreate}
                    onClick={() => document.getElementById("create-periode-timesheet")?.click()}
                  >
                    Pilih Excel Hivedesk
                  </Button>
                  <div className="text-xs text-muted-foreground truncate" title={timesheetFile?.name}>
                    {timesheetFile ? timesheetFile.name : "Belum ada file dipilih."}
                  </div>
                </div>

                {submittingCreate && (
                  <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                    <div className="text-xs font-medium text-muted-foreground">Progres</div>
                    <ul className="space-y-2">
                      {CREATE_FLOW_STEPS.map((step, idx) => {
                        const state = flowStepStates[idx];
                        return (
                          <li key={step.id} className="flex items-start gap-2 text-sm">
                            {state === "done" ? (
                              <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" aria-hidden />
                            ) : null}
                            {state === "running" ? (
                              <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0 mt-0.5" aria-hidden />
                            ) : null}
                            {state === "pending" ? (
                              <span
                                className="h-4 w-4 shrink-0 mt-0.5 rounded-full border border-muted-foreground/40 block"
                                aria-hidden
                              />
                            ) : null}
                            {state === "error" ? (
                              <span
                                className="h-4 w-4 shrink-0 mt-0.5 rounded-full border-2 border-destructive block"
                                aria-hidden
                              />
                            ) : null}
                            <span className={state === "error" ? "text-destructive" : ""}>{step.label}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!submittingCreate) {
                      resetCreateDialogForm();
                      setOpenCreate(false);
                    }
                  }}
                  disabled={submittingCreate}
                >
                  Batal
                </Button>
                <Button
                  onClick={() => void submitCreate()}
                  disabled={submittingCreate || !getAccessToken() || !timesheetFile}
                >
                  Simpan
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Daftar Periode</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-3 font-medium">Rentang</th>
                  <th className="py-3 font-medium">Status</th>
                  <th className="py-3 font-medium text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={3} className="py-8 text-center text-muted-foreground">
                      {loading ? "Memuat..." : "Belum ada periode"}
                    </td>
                  </tr>
                )}
                {sorted.map((p) => (
                  <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-3">
                      <div className="font-medium">{monthTitleFromEndDate(p.end_date)}</div>
                      <div className="text-xs text-muted-foreground">{formatRangeId(p)}</div>
                    </td>
                    <td className="py-3">
                      <Select
                        value={p.status}
                        onValueChange={(v) => void changeStatus(p.id, v as PeriodStatus)}
                        disabled={busyId === p.id}
                      >
                        <SelectTrigger className="w-[160px] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s.value} value={s.value}>
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 text-right">
                      <Button variant="outline" size="sm" asChild className="mr-1">
                        <Link to="/periode/$periodId" params={{ periodId: p.id }}>
                          Masuk
                        </Link>
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(p)} disabled={busyId === p.id}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus periode penggajian?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget ? (
                <>
                  Kamu akan menghapus <span className="font-medium text-foreground">{monthTitleFromEndDate(deleteTarget.end_date)}</span>{" "}
                  dengan rentang <span className="font-medium text-foreground">{formatRangeId(deleteTarget)}</span>. Tindakan ini tidak dapat dibatalkan.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>Batal</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTarget && void remove(deleteTarget.id)}
              disabled={deleteLoading}
            >
              {deleteLoading ? "Menghapus..." : "Hapus"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

